import React from 'react';
import { CustomAttributeValueType } from '@core/generated/gql-types';
import type { CustomAttr } from '@/types/customAttribute';
import { formatLabel } from '@/utils/utils';
import { parseAddressFromJson } from '@/modules/workers/utils/customAttributes.utils';
import { generateFriendlyAddress } from '@core/util/google-places.service';

interface CustomAttributeProps {
  attributeRef: string;
  attr?: CustomAttr;
  label?: string;
}

function formatCustomAttributeDisplay(
  rawValue: string | null | undefined,
  valueType: CustomAttributeValueType | null | undefined,
): string {
  const value = rawValue ?? '';
  if (value === '') {
    return '-';
  }

  const address = parseAddressFromJson(value);
  if (address) {
    return generateFriendlyAddress(address) || '-';
  }

  switch (valueType) {
    case CustomAttributeValueType.Date: {
      const millis = parseInt(value, 10);
      if (Number.isNaN(millis)) {
        return value;
      }
      return new Date(millis).toLocaleDateString(undefined, {
        dateStyle: 'medium',
      });
    }
    case CustomAttributeValueType.Boolean: {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') {
        return 'True';
      }
      if (normalized === 'false') {
        return 'False';
      }
      return value;
    }
    case CustomAttributeValueType.Float: {
      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) {
        return value;
      }
      return parsed.toLocaleString(undefined, { maximumFractionDigits: 6 });
    }
    case CustomAttributeValueType.String:
    default:
      return value;
  }
}

const CustomAttribute: React.FC<CustomAttributeProps> = ({
  attributeRef,
  attr,
  label,
}) => {
  const displayValue = formatCustomAttributeDisplay(
    attr?.value,
    attr?.valueType ?? undefined,
  );

  return (
    <div className="min-w-0">
      <dt className="text-sm font-medium text-gray-500">
        {label ?? formatLabel(attributeRef)}
      </dt>
      <dd className="mt-1 text-sm break-words text-gray-900">{displayValue}</dd>
    </div>
  );
};

export default CustomAttribute;
